import { rewordValidationMessages } from '#api/errors.ts';
import type { ResourceListOptions } from '#dn-types/api.ts';
import type { EndpointOIDC } from '#dn-types/endpoint-oidc.ts';
import { del, get, getList, post, put } from './fetch.ts';

export const DEFAULT_ENDPOINT_OIDC_PROVIDERS_PAGE_SIZE = 25;

// GET /endpoint-oidc-providers
export type GetEndpointOIDCProvidersResult = EndpointOIDC[];
export function getEndpointOIDCProviders({
  cursor = '',
  pageSize = DEFAULT_ENDPOINT_OIDC_PROVIDERS_PAGE_SIZE,
  init,
}: ResourceListOptions = {}) {
  return getList<GetEndpointOIDCProvidersResult>(
    `/v1/endpoint-oidc-providers?cursor=${encodeURIComponent(cursor)}&pageSize=${pageSize}`,
    init
  ).then((res) => {
    // We're not actually paginating, otherwise we would need to do the "paginating backwards two-step shuffle"
    return res;
  });
}

// GET /endpoint-oidc-providers/:id
export type GetEndpointOIDCProviderResult = EndpointOIDC;
export function getEndpointOIDCProvider(id: string, init?: RequestInit) {
  return get<GetEndpointOIDCProviderResult>(`/v1/endpoint-oidc-providers/${encodeURIComponent(id)}`, init);
}

// PUT /endpoint-oidc-providers/:id
export type EditEndpointOIDCProviderVariables = {
  id: string;
  values: {
    /** Requirement: <= 50 chars */
    name: string;
    /** Requirement: <= 255 chars */
    clientID: string;
    /** Requirement: <= 255 chars */
    clientSecret: string | null;
    /** Requirement: begins with https:// */
    issuerURL: string;
    enabled: boolean;
    networkID: string;
    roleID: string | null;
    /** Requirement: between 1 hour and 5 years (43800 hrs), or null for forever */
    authValidityHours: number | null;
    domainID: string | null;
    /** Requirement: each entry must exactly match one of the network's own CIDRs */
    autoAssignCIDRs: string[] | null;
  };
};
export type EditEndpointOIDCProviderResult = EndpointOIDC;
export function editEndpointOIDCProvider(input: EditEndpointOIDCProviderVariables) {
  const { id, values } = input;
  return put<EditEndpointOIDCProviderResult>(`/v1/endpoint-oidc-providers/${encodeURIComponent(id)}`, values).catch(
    (err: unknown) => {
      rewordEndpointOIDCProviderErrors(err, values);
    }
  );
}

// POST /endpoint-oidc-providers
export type AddEndpointOIDCProviderVariables = {
  /** Requirement: <= 50 chars */
  name: string;
  /** Requirement: <= 255 chars */
  clientID: string;
  /** Requirement: <= 255 chars */
  clientSecret: string;
  /** Requirement: begins with https:// */
  issuerURL: string;
  enabled: boolean;
  networkID: string;
  roleID: string | null;
  /** Requirement: between 1 hour and 5 years (43800 hrs), or null for forever */
  authValidityHours: number | null;
  domainID: string | null;
  /** Requirement: each entry must exactly match one of the network's own CIDRs */
  autoAssignCIDRs: string[] | null;
};
export type AddEndpointOIDCProviderResult = EndpointOIDC;
export function addEndpointOIDCProvider(formData: AddEndpointOIDCProviderVariables) {
  return post<AddEndpointOIDCProviderResult>('/v1/endpoint-oidc-providers', formData).catch((err: unknown) => {
    rewordEndpointOIDCProviderErrors(err, formData);
  });
}

// DELETE /endpoint-oidc-providers
export type DeleteOIDCProviderResult = Record<string, never>;
export function deleteEndpointOIDCProvider(id: string) {
  return del<DeleteOIDCProviderResult>(`/v1/endpoint-oidc-providers/${encodeURIComponent(id)}`);
}

function rewordEndpointOIDCProviderErrors(
  err: unknown,
  { issuerURL }: AddEndpointOIDCProviderVariables | EditEndpointOIDCProviderVariables['values']
): never {
  rewordValidationMessages(err, (details) => {
    if (details.path === 'name' && details.code === 'ERR_DUPLICATE_VALUE') {
      return 'You already have a provider with this name, try another';
    }
    if (details.path === undefined && details.code === 'ERR_DOMAIN_ALREADY_ENABLED') {
      return 'You already have an enabled provider with this domain, please disable it first';
    }
    if (details.path === undefined && details.code === 'ERR_LIMIT_EXCEEDED') {
      // If we got a limit from the api, we'll use that
      return `You have already created the maximum number of${typeof details.extra?.limit === 'number' ? ` ${details.extra.limit}` : ''} endpoint SSO providers`;
    }
    if (details.path === 'issuerURL' && details.code === 'ERR_OIDC_DISCOVERY_VALIDATION_FAILURE') {
      return `Could not find a valid discovery document at ${issuerURL.replace(
        /\/$/,
        ''
      )}/.well-known/openid-configuration`;
    }
    if (details.path === 'issuerURL' && details.code === 'ERR_OIDC_ISSUER_DISCOVERY_MISMATCH') {
      return `The "issuer" field found in the discovery document ${issuerURL.replace(
        /\/$/,
        ''
      )}/.well-known/openid-configuration does not match the one provided`;
    }
    if (details.path === 'issuerURL' && details.code === 'ERR_INVALID_HTTPS_URL') {
      return 'Please provide a valid URL';
    }
    if (details.path === 'domainID' && details.code === 'ERR_MISSING_VALUE') {
      return 'Please disable the provider before removing the domain';
    }
  });
}
